import { toast } from "react-toastify"

import { useTimezone } from "hooks/useTimezone"

import { GetSingleUser } from "services/user/type"

import { shortenString } from "util/formatter"

import { ReactComponent as CopyIcon } from "../../../assets/icons/copy-icon.svg"
import { ReactComponent as PencilIcon } from "../../../assets/icons/pencil.svg"

export interface UserInfoPlayerCardProps {
  user: GetSingleUser
  setOpenEditUser: (value: boolean) => void
}

export const UserInfoPlayerCard = ({ user, setOpenEditUser }: UserInfoPlayerCardProps) => {
  const { formatDate } = useTimezone()
  const isKycPending =
    user.kyc_files?.status === "PENDING" &&
    (user.kyc_files.doc_add_verify !== null ||
      user.kyc_files.doc_proof_address !== null ||
      user.kyc_files.img_idcard_back !== null ||
      user.kyc_files.img_idcard_front !== null)

  const vip = user.vipProgress
  const hasVipTier = !!vip && vip.tierId !== null

  const handleReferredByClick = () => {
    if (user.referred_by) {
      window.open(`/user/${user.referred_by.user_id}`, "_blank")
    }
  }

  return (
    <div className="flex flex-col gap-2 p-6 w-full rounded-[8px] border border-[rgba(0,0,0,0.1)] dark:border-transparent bg-[rgba(212,212,212,0.1)] dark:bg-[rgba(255,255,255,0.04)] text-white backdrop-blur-md">
      <div className="flex flex-row gap-4 text-black dark:text-white text-lg mb-4">
        Player Information
        <button className="flex items-center justify-center h-full text-black dark:text-white " onClick={() => setOpenEditUser(true)}>
          <PencilIcon className="w-5 h-5 hover:text-[rgba(188,43,228,0.8)] transition-all" />
        </button>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)] text-nowrap">Player ID</div>
          <button
            className="text-base text-black dark:text-white flex justify-end items-center gap-2 underline min-w-[150px] hover:text-blue-700 dark:hover:text-blue-700"
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(user.id)
              toast.success("Copied to clipboard!")
            }}
            title="Click to copy full value"
          >
            {shortenString(user.id)}
            <CopyIcon className="stroke-blue-700" />
          </button>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)]">Nickname</div>
          <div className="text-base text-black dark:text-white">{user.username}</div>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)] text-nowrap">Email</div>
          <button
            className="text-base text-black dark:text-white flex justify-end items-center gap-2 underline min-w-[150px] hover:text-blue-700 dark:hover:text-blue-700"
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(user.email_address)
              toast.success("Copied to clipboard!")
            }}
            title="Click to copy full value"
          >
            {user.email_address}
            <CopyIcon className="stroke-blue-700" />
          </button>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)] text-nowrap">Phone</div>
          <button
            className="text-base text-black dark:text-white flex justify-end items-center gap-2 underline min-w-[150px] hover:text-blue-700 dark:hover:text-blue-700"
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(user.phone_number)
              toast.success("Copied to clipboard!")
            }}
            title="Click to copy full value"
          >
            {user.phone_number}
            <CopyIcon className="stroke-blue-700" />
          </button>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)]">Default Language</div>
          <div className="text-base text-black dark:text-white">{user.site_lang}</div>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)]"> {hasVipTier ? "VIP Tier" : "VIP Level"}</div>
          <div className="text-base text-black dark:text-white">{user.vipLabel}</div>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)]">Tenant</div>
          <div className="text-base text-black dark:text-white">{user.tenant_id}</div>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)] text-nowrap">Created at</div>
          <div className="text-base text-black dark:text-white text-end">{formatDate(user.created_at.toString())}</div>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)]">KYC</div>
          <div className="text-base text-black dark:text-white">{user.kyc ? "Verified" : isKycPending ? " PENDING " : "Not verified"}</div>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)] text-nowrap">Referred by</div>
          <div className="text-base text-black dark:text-white">
            {user.referred_by ? (
              <button
                className="underline hover:text-blue-700 dark:hover:text-blue-700"
                onClick={handleReferredByClick}
                title={`Affiliate code: ${user.referred_by.affiliate_code}`}
              >
                {user.referred_by.username}
              </button>
            ) : (
              "—"
            )}
          </div>
        </div>
        <div className="flex justify-between gap-3">
          <div className="text-base text-black dark:text-[rgba(156,163,175,1)]">Stag</div>
          <div className="text-base text-black dark:text-white">{user.stag || "—"}</div>
        </div>
      </div>
    </div>
  )
}
